import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import Scroll from '../components/shared/Scroll';
import { ImageScaleView, ImageView } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { parseAmount } from 'src/lib/utils';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import StateObserver from 'src/StateObserver';
import { getGemsToPointsExchangeRates } from 'src/replicant/inventory/inventory.getters';
import * as Inventory from 'src/replicant/inventory';
import { hasAlternativeWaysToGetGems, PinchType } from './Helpers';
import { trackPinchEvent } from 'src/lib/analytics/events';
import { ExchangeItem } from 'src/replicant/data/GemsExchange.schema';

import { AssetGroup } from '@play-co/timestep-core';

type CloseHandler = (result: 'success' | 'failed' | 'close') => void;

/**
 * B-coins shop popup
 */
export default class PopupCoinsShop extends PopupBasic {
  private scroll: Scroll<ExchangeItem>;
  private buttonBack: ButtonScaleViewWithText;
  private close: CloseHandler;

  /**
   * Popup constructor
   */
  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => opts.close('close'),
      zIndex: 10000,
      width: uiConfig.popups.buruburu.width,
      height: 820,
      darkerBg: true,
      skipMessage: true,
      tracking: { feature: 'coins-shop', $subFeature: 'coins-shop-popup' },
      titleIcon: {
        image: 'assets/ui/shop/icons/bpoint_header.png',
        width: 60,
        height: 60,
      },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();

    this.scroll = new Scroll<ExchangeItem>({
      superview: this.box,
      createItem: this.createItem.bind(this),
      showBg: true,
      firstItemOffset: 76,
      margin: 15,
      rect: {
        x: (this.box.style.width - 512) * 0.5,
        y: 94,
        width: 512,
        height: 586,
      },
    });

    this.buttonBack = new ButtonScaleViewWithText({
      ...uiConfig.buttons.cancel,
      superview: this.box,
      labelOffsetY: 0,
      labelOffsetX: -9,
      fontSize: 50,
      font: bitmapFonts('Body'),
      x: this.box.style.width / 2,
      y: 742,
      width: 228,
      height: 94.7,
      centerOnOrigin: true,
      localeText: () => i18n('basic.close'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        opts.close('close');
      },
      tracking: { feature: 'coins-shop', $subFeature: 'coins-shop-back' },
    });

    this.createBanner();
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/shop/icons/bpoint_header.png',
      'assets/ui/shop/banners/shop_bpoint.png',
      'assets/ui/shop/frames/scroll_cell.png',
      'assets/ui/shop/frames/panel_unit_white.png',
      'assets/ui/shop/icons/bpoint_large.png',
      'assets/ui/shop/frames/panel_unit.png',
      'assets/ui/hud/icons/hud_gem.png',
    ]);
  }

  /**
   * On dialog init
   */
  public init(opts: { pinch?: PinchType }) {
    super.init({
      title: this.getTitle(opts.pinch),
    });

    this.scroll.setItems(getGemsToPointsExchangeRates());

    this.okButtonClick;
  }

  /**
   * Return title based on runout type
   */
  private getTitle(pinch: PinchType | undefined) {
    switch (pinch) {
      case 'bPoints':
        return i18n('currencyExchange.pinch.runOutOfbPoints');
      case 'energy':
        return i18n('currencyExchange.pinch.runOutOfEnergy');
      default:
        return i18n('currencyExchange.coinsShop.title');
    }
  }

  /**
   * Create banner
   */
  private createBanner() {
    new ImageView({
      superview: this.box,
      image: 'assets/ui/shop/banners/shop_bpoint.png',
      width: 505,
      height: 63,
      x: this.box.style.width / 2,
      y: 130,
      centerOnOrigin: true,
    });
  }

  /**
   * Create view item
   */
  private createItem(superview: View, _: number, item: ExchangeItem) {
    const view = new View({
      tag: 'item',
      superview,
      width: 485,
      height: 109,
    });

    const frame = new ImageScaleView({
      tag: 'frame',
      superview: view,
      image: 'assets/ui/shop/frames/scroll_cell.png',
      scaleMethod: '9slice',
      width: 485,
      height: 109,
      x: (superview.style.width - 485) * 0.5,
      sourceSlices: {
        horizontal: { left: 55, right: 55 },
        vertical: { top: 56.5, bottom: 56.5 },
      },
    });

    const rewardFrame = new ImageScaleView({
      tag: 'rewardFrame',
      superview: view,
      image: 'assets/ui/shop/frames/panel_unit_white.png',
      scaleMethod: '9slice',
      width: 167,
      height: 47,
      x: 45,
      y: 28,
      sourceSlices: {
        horizontal: { left: 22.5, right: 22.5 },
        vertical: { top: 22.5, bottom: 22.5 },
      },
    });

    new ImageView({
      tag: 'coin',
      superview: view,
      image: 'assets/ui/shop/icons/bpoint_large.png',
      width: 58,
      height: 58,
      x: 23,
      y: 23,
    });

    new LangBitmapFontTextView({
      tag: 'reward',
      superview: rewardFrame,
      centerAnchor: true,
      align: 'center',
      verticalAlign: 'center',
      color: '#9A7171',
      wordWrap: false,
      x: 34,
      y: 1,
      size: 26,
      width: 127,
      height: 44,
      font: bitmapFonts('Body'),
      localeText: () => `x ${parseAmount(item.items + item.bonus)}`,
      letterSpacing: -2,
    });

    new LangBitmapFontTextView({
      tag: 'bonus',
      superview: view,
      centerAnchor: true,
      align: 'left',
      verticalAlign: 'center',
      color: '#DBA98E',
      wordWrap: false,
      x: 82,
      y: 76,
      size: 15,
      width: 167,
      height: 16,
      font: bitmapFonts('Body'),
      localeText: () =>
        i18n('currencyExchange.coinsShop.bonus', {
          text: `${parseAmount(item.items)} + ${parseAmount(item.bonus)}`,
        }),
      letterSpacing: -2,
      visible: !!item.bonus,
    });

    const priceFrame = new ImageScaleView({
      tag: 'priceFrame',
      superview: view,
      image: 'assets/ui/shop/frames/panel_unit.png',
      scaleMethod: '9slice',
      width: 127.19,
      height: 50,
      x: 235,
      y: 27,
      sourceSlices: {
        horizontal: { left: 24, right: 24 },
        vertical: { top: 24, bottom: 24 },
      },
    });

    new LangBitmapFontTextView({
      tag: 'price',
      superview: priceFrame,
      centerAnchor: true,
      align: 'center',
      verticalAlign: 'center',
      color: '#FBFEFA',
      wordWrap: false,
      x: 39,
      y: 8,
      size: 20,
      width: 80,
      height: 36,
      font: bitmapFonts('Body'),
      localeText: () => `x ${parseAmount(item.premium)}`,
      letterSpacing: -2,
    });

    new ImageView({
      tag: 'gem',
      superview: priceFrame,
      image: 'assets/ui/hud/icons/hud_gem.png',
      width: 50,
      height: 42,
      scale: 0.85,
      x: 1,
      y: 10,
    });

    const button = new ButtonScaleViewWithText({
      tag: 'exchangeBtn',
      ...uiConfig.buttons.secondary,
      superview: view,
      fontSize: 25,
      labelOffsetX: -3,
      font: bitmapFonts('Body'),
      x: 374,
      y: 17,
      width: 112,
      height: 74,
      pressedOffsetY: 2,
      localeText: () => i18n('currencyExchange.coinsShop.exchangeButton'),
      onClick: async () => {
        sounds.sfx.play('ok');

        // Show earned dialog
        await this.okButtonClick(item.premium, item);
      },
      tracking: { feature: 'coins-shop', $subFeature: 'coins-shop-exchange' },
    });

    // We trying to keep this button active to show alternative ways to get gems
    // But if user cant afford it and don't have alternative ways to get gems(rare case)
    // we should disable this button to avoid bad user experience, where UI do not react on user input
    if (this.hasEnoughGems(item.premium) || hasAlternativeWaysToGetGems()) {
      button.setDisabled(false);
    } else {
      button.setDisabled(true);
    }

    return view;
  }

  /**
   * Check if we has has enough gems
   */
  private hasEnoughGems(gemsRequired: number): boolean {
    const { user } = StateObserver.getState();
    const gems = Inventory.getters.getGems(user);

    return gems >= gemsRequired;
  }

  /**
   * On ok button click
   */
  private async okButtonClick(gemsRequired: number, item: ExchangeItem) {
    if (this.hasEnoughGems(gemsRequired))
      return await this.onSuccessPurchase(item);

    // Pinch analytics
    trackPinchEvent({
      resource: 'gems',
      trigger: 'shop_bPoints',
      product_id: `bPoints_${item.items + item.bonus}`,
    });

    // Failed purchase item
    this.close('failed');
  }

  /**
   * On success purchase
   */
  private async onSuccessPurchase(item: ExchangeItem) {
    // Show earned dialog
    await openPopupPromise('popupCoinsExchanged', {
      items: item.items,
      bonus: item.bonus,
      price: item.premium,
    });

    // Successfully purchase close
    this.close('success');
  }
}
